import { Fragment, type ReactNode } from "react";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { useIsSmUp } from "@/lib/use-media-query";

// 185: shared table->cards reflow. Below `sm` a list renders as stacked cards
// (label:value pairs) instead of a horizontal-scroll table, so the columns that
// matter (date/time, the View action) are visible without scrolling sideways.
//
// A single `CardColumn[]` drives BOTH the desktop table and the mobile card, so
// the two views cannot drift. `DataTable` reuses `RowCards` for its own card
// mode; raw-`<Table>` pages use `ResponsiveTable`.

export interface CardColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /**
   * Card-mode hint (desktop table is unaffected):
   * - "title": rendered as the card heading (defaults to the first column).
   * - "action": grouped top-right in the card (e.g. the View / row actions).
   * - "hidden": omitted from the card (e.g. a redundant column).
   * Unset columns become label:value rows; a column with an empty header
   * renders value-only (full width).
   */
  cardRole?: "title" | "action" | "hidden";
  className?: string;
  headerClassName?: string;
}

interface SharedProps<T> {
  rows: T[];
  rowKey: (row: T) => string;
  /** Extra attributes (e.g. data-item / data-item-id) attached to each row/card. */
  rowProps?: (row: T) => Record<string, string | undefined>;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
}

export interface RowCardsProps<T> extends SharedProps<T> {
  columns: CardColumn<T>[];
}

/** The mobile card list (rendered behind `sm:hidden` by callers). */
export function RowCards<T>({ columns, rows, rowKey, rowProps, onRowClick, rowClassName }: RowCardsProps<T>) {
  const titleCol = columns.find((c) => c.cardRole === "title") ?? columns[0];
  const actionCols = columns.filter((c) => c.cardRole === "action");
  const bodyCols = columns.filter(
    (c) => c !== titleCol && c.cardRole !== "action" && c.cardRole !== "hidden",
  );

  return (
    <div className="flex flex-col gap-2">
      {rows.map((row) => (
        <div
          key={rowKey(row)}
          className={`rounded-lg border border-border bg-card p-3 ${onRowClick ? "cursor-pointer active:bg-muted/40" : ""} ${rowClassName?.(row) ?? ""}`}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          {...(rowProps?.(row) ?? {})}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1 font-medium">{titleCol?.render(row)}</div>
            {actionCols.length > 0 && (
              // Actions must not trigger an onRowClick navigation.
              <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
                {actionCols.map((c) => (
                  <span key={c.key}>{c.render(row)}</span>
                ))}
              </div>
            )}
          </div>
          {bodyCols.length > 0 && (
            <dl className="mt-2 grid grid-cols-[minmax(0,auto)_1fr] gap-x-3 gap-y-1 text-sm">
              {bodyCols.map((c) => {
                const hasLabel = c.header != null && c.header !== "";
                if (!hasLabel) {
                  return (
                    <dd key={c.key} className="col-span-2 min-w-0">
                      {c.render(row)}
                    </dd>
                  );
                }
                return (
                  <Fragment key={c.key}>
                    <dt className="text-muted-foreground">{c.header}</dt>
                    <dd className="min-w-0 text-right">{c.render(row)}</dd>
                  </Fragment>
                );
              })}
            </dl>
          )}
        </div>
      ))}
    </div>
  );
}

export interface ResponsiveTableProps<T> extends SharedProps<T> {
  columns: CardColumn<T>[];
}

/**
 * Table on `sm+`, stacked cards below `sm`, both driven by one `columns` array.
 * For raw-`<Table>` list pages that own their own data/filtering (so they don't
 * need `DataTable`'s server fetch/sort/pagination). Callers guard the empty
 * state themselves and only render this when there are rows.
 */
export function ResponsiveTable<T>({ columns, rows, rowKey, rowProps, onRowClick, rowClassName }: ResponsiveTableProps<T>) {
  const smUp = useIsSmUp();

  // Render ONE tree (table OR cards), not both -- see use-media-query.ts.
  if (!smUp) {
    return (
      <RowCards
        columns={columns}
        rows={rows}
        rowKey={rowKey}
        rowProps={rowProps}
        onRowClick={onRowClick}
        rowClassName={rowClassName}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key} className={col.headerClassName}>
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={rowKey(row)}
              className={`${onRowClick ? "cursor-pointer" : ""} ${rowClassName?.(row) ?? ""}`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              {...(rowProps?.(row) ?? {})}
            >
              {columns.map((col) => (
                <TableCell key={col.key} className={col.className}>
                  {col.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
